import React, { useState, useEffect, Fragment } from "react";
import { bool, string } from "prop-types";
import { useDispatch, connect } from "react-redux";
import { useParams } from "react-router-dom";
import { compose } from "redux";
import withExchangeToken from "pages/_components/withExchangeToken";
import { Field, Form, Formik } from "formik";
import TextField from "pages/_components/fields/TextField";
import Button from "pages/_components/Button";
import { selectors as onboardingSelectors } from "reducers/onboarding";
import { actions as modalActions } from "reducers/modal";
import * as Yup from "yup";
import * as i18nUtils from "util/i18n";
import Logo from "pages/login/_components/Logo";

// import * as configUtils from "util/config";
import { push } from "connected-react-router";
import { actions as recoveryPasswordActions } from "reducers/recoveryPassword";

import ToDoList from "pages/onboarding/_components/ToDoList";
import ErrorPopup from "pages/onboarding/_components/ErrorPopup";
import { Mixpanel } from "util/clickstreaming";
import I18n from "pages/_components/I18n";

const FORM_ID = "recoveryPassword.step3";

const recoveryI18n = {
    password: "recoveryPassword",
    username: "recoveryUsername",
};

const recoveryText = {
    password: "contraseña",
    username: "usuario",
};

const StepUser = ({ isMobile, isUsernameAvailable, exchangeToken, resetCode }) => {
    const dispatch = useDispatch();
    const [isValidating, setIsValidating] = useState(false);
    const [error, setError] = useState("");
    const { recoveryType } = useParams();

    useEffect(() => {
        if (isValidating && isUsernameAvailable && isUsernameAvailable === "USERNAME_AVAILABLE") {
            setIsValidating(false);
            dispatch(push("/onboarding/stepPassword"));
        } else if (isValidating && isUsernameAvailable && isUsernameAvailable === "USERNAME_UNAVAILABLE") {
            setIsValidating(false);
            setError(
                i18nUtils.get(
                    "onboarding.step.verifyUsername.errors.usernameUnavailable",
                    "Nombre de usuario ya utilizado, pruebe con otro.",
                ),
            );
            dispatch(modalActions.modalShow());
        } else if (isValidating && isUsernameAvailable?.length) {
            setIsValidating(false);
            setError(isUsernameAvailable);
            dispatch(modalActions.modalShow());
        }
    }, [isValidating, isUsernameAvailable]);

    const validations = [
        {
            description: i18nUtils.get("onboarding.user.validation1", "Primera letra mayúscula"),
            status: (user) => {
                if (!user) {
                    return "INITIAL";
                }
                return user[0] === user[0]?.toUpperCase() && user[0] !== user[0]?.toLowerCase() ? "DONE" : "WRONG";
            },
        },
        {
            description: i18nUtils.get("onboarding.user.validation2", "Letras y números"),
            status: (user) => {
                if (!user) {
                    return "INITIAL";
                }
                return /\d/.test(user) && /[a-zA-Z]/.test(user) ? "DONE" : "WRONG";
            },
        },
        {
            description: i18nUtils.get("onboarding.user.validation3", "Mínimo 5 caracteres"),
            status: (user) => {
                if (!user) {
                    return "INITIAL";
                }
                return user.length >= 5 ? "DONE" : "WRONG";
            },
        },
    ];

    const getItems = (user) => validations.map((val) => ({ status: val.status(user), description: val.description }));

    const submitIsDisabled = (user) => validations.some((val) => val.status(user) !== "DONE") || user.length < 5;

    const handleClickButtonClose = () => {
        dispatch(modalActions.modalClose());
    };

    const formikProps = {
        validateOnChange: false,
        validateOnBlur: true,
        initialValues: {
            username: "",
            usernameConfirmation: "",
        },
        validationSchema: Yup.lazy((values) =>
            Yup.object().shape({
                username: Yup.string().oneOf(
                    [values.usernameConfirmation],
                    i18nUtils.get("recovery.step3.username.error.usernameConfirmationMustMatch"),
                ),
                usernameConfirmation: Yup.string().oneOf(
                    [values.username],
                    i18nUtils.get("recovery.step3.username.error.usernameConfirmationMustMatch"),
                ),
            }),
        ),
        onSubmit: (values, formikBag) => {
            Mixpanel.track(FORM_ID);

            dispatch(
                recoveryPasswordActions.recoveryPassStep3(
                    values.username,
                    values.usernameConfirmation,
                    exchangeToken,
                    resetCode,
                    formikBag,
                    recoveryType,
                ),
            );
        },
    };

    return (
        <Fragment>
            {!isMobile && (
                <div className="text-center">
                    <Logo className="svg-image logo" />
                </div>
            )}
            <h1 className="text-center">
                <I18n
                    id={`${recoveryI18n[recoveryType]}.step1.header`}
                    defaultValue={`Olvidaste tu ${recoveryText[recoveryType]}?`}
                />
            </h1>
            <br />
            <br />
            <Formik {...formikProps}>
                {(allFormik) => (
                    <Form className="above-the-fold">
                        <Field
                            component={TextField}
                            name="username"
                            labelTextDefault={i18nUtils.get("onboarding.user.label", "Nuevo nombre de usuario")}
                            placeholder={i18nUtils.get("onboarding.user.placeholder", "ej: Tuempresa22")}
                            autoFocus={!isMobile}
                            spaceEnabled={false}
                        />
                        <br />
                        <br />
                        <ToDoList items={getItems(allFormik?.values?.user)} />
                        <br />
                        <Field
                            component={TextField}
                            name="usernameConfirmation"
                            labelTextDefault={i18nUtils.get(
                                "onboarding.user.label",
                                "Vuelve a escribir el nuevo nombre de usuario",
                            )}
                            placeholder={i18nUtils.get("onboarding.user.placeholder", "ej: Tuempresa22")}
                            autoFocus={!isMobile}
                            spaceEnabled={false}
                        />
                        <br />
                        <br />
                        <div className="form-actions">
                            <Button
                                bsStyle="primary"
                                label="global.continue"
                                loading={isValidating || allFormik?.isSubmitting}
                                type="submit"
                                block
                                disabled={
                                    submitIsDisabled(allFormik?.values?.username) ||
                                    !allFormik?.values?.usernameConfirmation
                                }
                            />
                        </div>
                    </Form>
                )}
            </Formik>
            <ErrorPopup error={error} handleClick={handleClickButtonClose} />
        </Fragment>
    );
};

StepUser.propTypes = {
    isMobile: bool.isRequired,
    isUsernameAvailable: string.isRequired,
    exchangeToken: string.isRequired,
    resetCode: string.isRequired,
};

const mapStateToProps = (state) => ({
    isUsernameAvailable: onboardingSelectors.getIsUsernameAvailable(state),
});
export default compose(connect(mapStateToProps))(withExchangeToken(StepUser));
